import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  CircleAlert,
  CircleCheck,
  Clock,
  FilePen,
  FileText,
  Hourglass,
  LayoutGrid,
  List,
  MoreVertical,
  Plus,
  Search,
  Send,
} from "lucide-react";
import client, { unwrap } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { formatINR, initials } from "@/lib/format";
import { QUOTATIONS_HERO_IMAGE, roomImageForSeed } from "@/lib/stockImages";
import PageHero from "@/components/layout/PageHero";
import KpiCard from "@/components/dashboard/KpiCard";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const PAGE_SIZE = 8;
const ALL = "all";
// Approved and finalized quotations are both done internally but not yet shared.
const READY = "READY";
const READY_STATUSES = ["APPROVED", "FINALIZED"];
const matchesStatus = (q, filter) =>
  filter === ALL || (filter === READY ? READY_STATUSES.includes(q.status) : q.status === filter);

const STATUS = {
  DRAFT: { label: "Draft", icon: FilePen, className: "bg-secondary text-muted-foreground" },
  SUBMITTED_FOR_APPROVAL: { label: "Pending approval", icon: Clock, className: "bg-accent/10 text-accent" },
  CHANGES_REQUIRED: { label: "Changes requested", icon: CircleAlert, className: "bg-danger/10 text-danger" },
  APPROVED: { label: "Approved", icon: CircleCheck, className: "bg-primary/10 text-primary" },
  FINALIZED: { label: "Finalized", icon: BadgeCheck, className: "bg-success/10 text-success" },
  SHARED: { label: "Sent", icon: Send, className: "bg-[hsl(214_60%_93%)] text-[hsl(214_45%_38%)]" },
};

const DATE_RANGES = {
  [ALL]: { label: "Any time", days: null },
  "30": { label: "Last 30 days", days: 30 },
  "90": { label: "Last 90 days", days: 90 },
  month: { label: "This month", days: null },
};

// Quotations have no human-friendly number yet, so show a short stable ref from the id.
const quoteRef = (q) => `#${String(q._id).slice(-6).toUpperCase()}`;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const projectMeta = (q) => [q.projectBhk, q.projectCity].filter(Boolean).join(" · ");

function inRange(date, rangeKey) {
  if (rangeKey === ALL) return true;
  const d = new Date(date);
  const now = new Date();
  if (rangeKey === "month") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  return now - d <= DATE_RANGES[rangeKey].days * 86400000;
}

function StatusPill({ status }) {
  const meta = STATUS[status] || STATUS.DRAFT;
  const Icon = meta.icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap", meta.className)}>
      <Icon className="h-3.5 w-3.5" /> {meta.label}
    </span>
  );
}

function Initials({ name, className }) {
  return (
    <span className={cn("rounded-full bg-secondary text-foreground/80 font-medium flex items-center justify-center shrink-0", className)}>
      {initials(name)}
    </span>
  );
}

function RowMenu({ q }) {
  const navigate = useNavigate();
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/quote/${q.publicToken}`);
      toast.success("Customer link copied");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`More actions for ${quoteRef(q)}`}
          onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => navigate(`/quotations/${q._id}`)}>Open quotation</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/projects/${q.projectId}`)}>Open project</DropdownMenuItem>
        {q.customerId && <DropdownMenuItem onClick={() => navigate(`/customers/${q.customerId}`)}>Open customer</DropdownMenuItem>}
        {q.status === "SHARED" && q.publicToken && <DropdownMenuItem onClick={copyLink}>Copy customer link</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function QuotationCard({ q }) {
  return (
    <Link
      to={`/quotations/${q._id}`}
      data-testid={`quotation-card-${q._id}`}
      className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
    >
      <div className="relative h-32">
        <img src={roomImageForSeed(q.projectId)} alt="" loading="lazy" className="h-full w-full object-cover" />
        <span className="absolute top-3 left-3"><StatusPill status={q.status} /></span>
      </div>
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-medium text-foreground">{quoteRef(q)} <span className="text-xs text-muted-foreground">v{q.currentVersion}</span></p>
          <p className="font-serif text-lg text-foreground">{formatINR(q.grandTotal)}</p>
        </div>
        <p className="text-sm text-muted-foreground truncate">{q.projectName}{projectMeta(q) && ` · ${projectMeta(q)}`}</p>
        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
          <span className="truncate">{q.customerName || "—"}</span>
          <span>{q.sentAt ? `Sent ${formatDate(q.sentAt)}` : `Created ${formatDate(q.createdAt)}`}</span>
        </div>
      </div>
    </Link>
  );
}

function NewQuotationDialog({ open, setOpen }) {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState("");
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => client.get("/projects").then(unwrap),
    enabled: open,
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-quotation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New Quotation</DialogTitle>
          <DialogDescription>
            Quotations are generated from a project's cart. Pick the project to build it in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="new-quotation-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" disabled={!projectId} onClick={() => navigate(`/projects/${projectId}`)}>
            Go to project <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function QuotationsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [dateRange, setDateRange] = useState(ALL);
  const [view, setView] = useState("list");
  const [page, setPage] = useState(1);
  const [newOpen, setNewOpen] = useState(false);

  const { data: quotations = [], isLoading } = useQuery({
    queryKey: ["quotations-all"],
    queryFn: () => client.get("/quotations").then(unwrap),
  });

  const counts = useMemo(() => {
    const c = {};
    quotations.forEach((q) => (c[q.status] = (c[q.status] || 0) + 1));
    return c;
  }, [quotations]);
  const totalValue = quotations.reduce((sum, q) => sum + (q.grandTotal || 0), 0);
  const sentValue = quotations.filter((q) => q.status === "SHARED").reduce((sum, q) => sum + (q.grandTotal || 0), 0);
  const approvedCount = (counts.APPROVED || 0) + (counts.FINALIZED || 0);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return quotations
      .filter((q) => matchesStatus(q, statusFilter))
      .filter((q) => inRange(q.createdAt, dateRange))
      .filter(
        (q) =>
          !term ||
          [q.customerName, q.customerPhone, q.projectName, quoteRef(q)].some((v) => v?.toLowerCase().includes(term)),
      );
  }, [quotations, statusFilter, dateRange, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filtersActive = search || statusFilter !== ALL || dateRange !== ALL;

  const withReset = (fn) => (v) => {
    fn(v);
    setPage(1);
  };
  const toggleStatus = (status) => withReset(setStatusFilter)(statusFilter === status ? ALL : status);
  const clearFilters = () => {
    setSearch("");
    setStatusFilter(ALL);
    setDateRange(ALL);
    setPage(1);
  };

  return (
    <div data-testid="quotations-page" className="space-y-6">
      <div>
        <PageHero
          image={QUOTATIONS_HERO_IMAGE}
          tagline="Beautiful homes begin with the right choices."
          title="Quotations"
          subtitle="Every proposal sent to a customer, across all your projects."
          action={
            hasPermission("project:manage") && (
              <Button data-testid="new-quotation-button" className="rounded-full px-5" onClick={() => setNewOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" /> New Quotation
              </Button>
            )
          }
        />

        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6 xl:-mt-14 xl:px-4">
          <KpiCard
            testId="kpi-total-quotations"
            icon={FileText}
            tone="green"
            label="Total Quotations"
            value={quotations.length}
            sublabel={`${formatINR(totalValue)} quoted`}
            onClick={() => withReset(setStatusFilter)(ALL)}
          />
          <KpiCard
            testId="kpi-sent-quotations"
            icon={Send}
            tone="terracotta"
            label="Sent to Customers"
            value={counts.SHARED || 0}
            sublabel={`${formatINR(sentValue)} with customers`}
            onClick={() => toggleStatus("SHARED")}
            active={statusFilter === "SHARED"}
          />
          <KpiCard
            testId="kpi-pending-approval"
            icon={Hourglass}
            tone="danger"
            label="Pending Approval"
            value={counts.SUBMITTED_FOR_APPROVAL || 0}
            sublabel={counts.SUBMITTED_FOR_APPROVAL ? "Need your review" : "All clear"}
            sublabelTone={counts.SUBMITTED_FOR_APPROVAL ? "text-danger" : "text-success"}
            onClick={() => toggleStatus("SUBMITTED_FOR_APPROVAL")}
            active={statusFilter === "SUBMITTED_FOR_APPROVAL"}
          />
          <KpiCard
            testId="kpi-approved-quotations"
            icon={BadgeCheck}
            tone="green"
            label="Ready to Send"
            value={approvedCount}
            sublabel={approvedCount ? "Approved, not yet shared" : "Nothing waiting"}
            onClick={() => toggleStatus(READY)}
            active={statusFilter === READY}
          />
        </div>
      </div>

      <section className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 border-b border-border">
          <div className="relative flex-1 lg:max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="quotations-search-input"
              value={search}
              onChange={(e) => withReset(setSearch)(e.target.value)}
              placeholder="Search by customer, project, ref…"
              className="w-full rounded-full border border-border bg-secondary/70 pl-10 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={statusFilter} onValueChange={withReset(setStatusFilter)}>
              <SelectTrigger data-testid="quotations-status-filter" className="w-[180px] rounded-lg bg-card"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                <SelectItem value={READY}>Ready to send{approvedCount ? ` (${approvedCount})` : ""}</SelectItem>
                {Object.entries(STATUS).map(([key, s]) => (
                  <SelectItem key={key} value={key}>{s.label}{counts[key] ? ` (${counts[key]})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={dateRange} onValueChange={withReset(setDateRange)}>
              <SelectTrigger data-testid="quotations-date-filter" className="w-[160px] rounded-lg bg-card"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(DATE_RANGES).map(([key, r]) => <SelectItem key={key} value={key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {filtersActive && (
              <button data-testid="quotations-clear-filters" onClick={clearFilters} className="text-sm font-medium text-accent hover:underline px-2">
                Clear filters
              </button>
            )}
          </div>
          <div className="hidden md:flex items-center gap-1 lg:ml-auto rounded-lg border border-border p-0.5" role="group" aria-label="View">
            {[["list", List], ["grid", LayoutGrid]].map(([key, Icon]) => (
              <button
                key={key}
                aria-label={`${key} view`}
                aria-pressed={view === key}
                data-testid={`quotations-view-${key}`}
                onClick={() => setView(key)}
                className={cn("p-2 rounded-md transition-colors", view === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>

        {view === "list" ? (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/50 text-muted-foreground text-[11px] uppercase tracking-wider text-left">
                    <th className="py-3 pl-5 pr-3 font-semibold">Quotation</th>
                    <th className="py-3 px-3 font-semibold">Customer</th>
                    <th className="py-3 px-3 font-semibold">Value</th>
                    <th className="py-3 px-3 font-semibold">Status</th>
                    <th className="py-3 px-3 font-semibold">Created</th>
                    <th className="py-3 px-3 font-semibold">Sent on</th>
                    <th className="py-3 px-3 font-semibold">Created by</th>
                    <th className="py-3 pl-3 pr-5 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pageItems.map((q) => (
                    <tr
                      key={q._id}
                      data-testid={`quotation-row-${q._id}`}
                      onClick={() => navigate(`/quotations/${q._id}`)}
                      className="hover:bg-secondary/40 cursor-pointer transition-colors"
                    >
                      <td className="py-3 pl-5 pr-3">
                        <div className="flex items-center gap-3">
                          <img src={roomImageForSeed(q.projectId)} alt="" loading="lazy" className="h-12 w-16 rounded-lg object-cover shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-foreground whitespace-nowrap">
                              {quoteRef(q)} <span className="text-xs font-normal text-muted-foreground">v{q.currentVersion}</span>
                            </p>
                            <p className="text-xs text-muted-foreground truncate max-w-[180px]">{q.projectName}</p>
                            {projectMeta(q) && <p className="text-xs text-muted-foreground">{projectMeta(q)}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2.5">
                          <Initials name={q.customerName} className="h-8 w-8 text-xs" />
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate max-w-[160px]">{q.customerName || "—"}</p>
                            {q.customerPhone && <p className="text-xs text-muted-foreground">{q.customerPhone}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 font-serif text-base text-foreground whitespace-nowrap tabular-nums">
                        ₹{(q.grandTotal || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="py-3 px-3"><StatusPill status={q.status} /></td>
                      <td className="py-3 px-3 text-muted-foreground whitespace-nowrap">{formatDate(q.createdAt)}</td>
                      <td className="py-3 px-3 text-muted-foreground whitespace-nowrap">{formatDate(q.sentAt)}</td>
                      <td className="py-3 px-3">
                        {q.createdByName ? (
                          <div className="flex items-center gap-2">
                            <Initials name={q.createdByName} className="h-7 w-7 text-[10px]" />
                            <span className="text-foreground/85 whitespace-nowrap">{q.createdByName}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 pl-3 pr-5">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link
                            to={`/quotations/${q._id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-accent hover:border-accent transition-colors"
                          >
                            View <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                          <RowMenu q={q} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="md:hidden divide-y divide-border">
              {pageItems.map((q) => (
                <li key={q._id}>
                  <Link to={`/quotations/${q._id}`} data-testid={`quotation-mobile-${q._id}`} className="flex gap-3 p-4">
                    <img src={roomImageForSeed(q.projectId)} alt="" loading="lazy" className="h-14 w-14 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-foreground truncate">{q.projectName}</p>
                        <p className="font-serif text-foreground shrink-0">{formatINR(q.grandTotal)}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{quoteRef(q)} · {q.customerName || "—"}</p>
                      <div className="mt-2"><StatusPill status={q.status} /></div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 p-4">
            {pageItems.map((q) => <QuotationCard key={q._id} q={q} />)}
          </div>
        )}

        {isLoading && <p className="px-6 py-10 text-center text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && filtered.length === 0 && (
          <div className="py-14 text-center">
            <p className="text-sm text-muted-foreground">
              {quotations.length === 0 ? "No quotations yet — generate one from a project's cart." : "No quotations match these filters."}
            </p>
            {filtersActive && <button onClick={clearFilters} className="mt-2 text-sm font-medium text-accent hover:underline">Clear filters</button>}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} quotation
              {filtered.length === 1 ? "" : "s"}
            </p>
            <Pagination testId="quotations-pagination" page={currentPage} pageCount={pageCount} onChange={setPage} />
          </div>
        )}
      </section>

      <NewQuotationDialog open={newOpen} setOpen={setNewOpen} />
    </div>
  );
}
